import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardHeader,
  CardContent,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useQuery } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import ReusableTable from "../../../components/ReusableTable/ReusableTable";
import InformationStepsCard from "../../../components/InformationStepsCard";
import EnrollDeviceDialog from "../../../components/EnrollDeviceDialog";
import AssetDetailsDialog from "../../../components/AssetDetailsDialog";
import { GET_MY_ASSETS } from "../../../apis/settingsApis/assets-apis/myAssetsApis";
import { WATCH_TOWER_STATUS } from "../../../apis/settingsApis/assets-apis/assetsApis";
import { GET_USER_DETAILS } from "../../../apis/dashboard-apis/dashboardDetailApi";
import {
  buildAssetColumns,
  makeRenderAssetCell,
  filterAssets,
  isAssetInScope,
} from "../assetTableConfig";
import { ASSET_OUT_OF_SCOPE_EFFECT } from "../../../utils/enums";

const EmployeeAssets = () => {
  const theme = useTheme();
  const { user } = useAuth0();
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [scopeTab, setScopeTab] = useState("active");
  const { data: meData } = useQuery(GET_USER_DETAILS);
  const me = meData?.getAccountDetails?.userDetails;

  // Gates the whole enrollment path. Defaults to hidden until the query answers,
  // so the button never flashes up on a tenant that has WatchTower switched off.
  const { data: watchTowerData } = useQuery(WATCH_TOWER_STATUS, {
    fetchPolicy: "cache-and-network",
  });
  const watchTowerEnabled = watchTowerData?.watchTowerStatus?.enabled === true;


  const { data, loading, refetch } = useQuery(GET_MY_ASSETS, {
    fetchPolicy: "cache-and-network",
  });

  const rows = useMemo(() => data?.getMyAssets ?? [], [data]);

  // Keep an open details dialog pointed at the freshest row after a refetch.
  useEffect(() => {
    setSelectedAsset((prev) => {
      if (!prev) return prev;
      const key = prev.assetId || prev.id;
      return rows.find((r) => (r.assetId || r.id) === key) || prev;
    });
  }, [rows]);
  const filteredRows = useMemo(() => filterAssets(rows, searchQuery), [rows, searchQuery]);
  const scopedRows = useMemo(
    () => filteredRows.filter((r) =>
      scopeTab === "inactive" ? !isAssetInScope(r) : isAssetInScope(r)
    ),
    [filteredRows, scopeTab]
  );
  const deviceCount = scopedRows.length;

  const employeeForDialog = useMemo(
    () => ({
      _originalEmployee: { employee_data_id: me?.employee_data_id },
      name: me?.name,
      email: user?.email,
    }),
    [me, user]
  );

  // Out-of-scope (inactive) devices aren't monitored — hide check columns like admin view.
  const columns = useMemo(
    () => [
      ...buildAssetColumns({
        showOwner: false,
        showChecks: scopeTab === "active",
        reservedWidth: 6, // the action column below
      }),
      { label: "", field: "action", width: "6%", align: "left" },
    ],
    [scopeTab]
  );
  const renderAssetCell = useMemo(() => makeRenderAssetCell(theme), [theme]);

  const renderCell = (row, column) => {
    if (column.field === "action") {
      return (
        <Tooltip title="View details" arrow>
          <IconButton
            size="small"
            onClick={() => setSelectedAsset(row)}
            sx={{
              color: theme.customColors.icons.dark,
              "&:hover": { backgroundColor: theme.customColors.background.light },
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: "16px" }} />
          </IconButton>
        </Tooltip>
      );
    }
    return renderAssetCell(row, column);
  };

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Box sx={{ mb: 3 }}>
        <InformationStepsCard
          heading="**What should I do on this page?**"
          subtitle="The devices you've registered with Rego WatchTower, plus any managed by your organization's MDM."
          steps={[
            // These three point at the Connect Device button, so they go when it
            // does — otherwise the card tells people to click something that isn't
            // on the page. The rest of the card is about the list itself and
            // applies either way, MDM-managed devices included.
            // The Connect Device dialog already walks through download → token →
            // register, so repeating it here just gives two copies to keep in step.
            watchTowerEnabled &&
              "To connect your device to Rego WatchTower, click **Connect Device** and follow the steps shown there.",
            watchTowerEnabled &&
              "Once connected, WatchTower reviews your device's security settings on its own. It never reads your personal files, photos or browsing history.",
            "Your device then appears in the list below, along with when it last reported in.",
            watchTowerEnabled &&
              "On a Mac, to remove Rego WatchTower later: click **Connect Device** and open **Uninstall the agent (macOS)** for the commands to run.",
            "If a device is failing any security requirement, open it. Each failing check shows the reason it failed, and a link to the steps that fix it — just follow them in order.",
            "The list has two tabs. **In Scope** is your devices that come under your organization's compliance area — these are the ones to keep secure and up to date.",
            // Same definition and the same info icon as the admin Assets page,
            // so both audiences read one explanation of what scope means.
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
              <strong>Out of Scope</strong>&nbsp;is devices that do not come under your
              organization's compliance area — for example ones that have been disposed of. You
              don't need to do anything for these.
              <Tooltip title={ASSET_OUT_OF_SCOPE_EFFECT} arrow placement="top">
                <InfoOutlinedIcon
                  sx={{ fontSize: 16, color: theme.customColors.text.secondary, cursor: "help" }}
                />
              </Tooltip>
            </Box>,
          ].filter(Boolean)}
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="h5" fontWeight="600" color={theme.customColors.text.strong}>
                My Devices
              </Typography>
              <ToggleButtonGroup
                value={scopeTab}
                exclusive
                size="small"
                onChange={(_, next) => next && setScopeTab(next)}
                aria-label="device status filter"
                sx={{
                  borderRadius: "26px",
                  border: "1px solid #E8EAED",
                  gap: 0.2,
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: "36px",
                    color: "#6B7280",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    px: 3,
                    py: 1,
                    minWidth: "70px",
                    transition: "all 0.2s ease-in-out",
                    "&.Mui-selected": {
                      backgroundColor: "#3B82F6",
                      color: "#fff",
                      boxShadow: "inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                      "&:hover": { backgroundColor: "#2563EB" },
                    },
                    "&:hover:not(.Mui-selected)": { backgroundColor: "#F9FAFB", color: "#374151" },
                  },
                }}
              >
                <ToggleButton value="active">In Scope</ToggleButton>
                <ToggleButton value="inactive">Out of Scope</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          }
          sx={{
            borderBottom: "1px solid #ddd",
            background: "#F7F7FC",
            ".MuiCardHeader-title": { fontSize: "16px", fontWeight: "bold" },
            padding: "14px",
          }}
          action={
            // Nothing to enrol into until an admin turns Rego WatchTower on for
            // the tenant, and the enrollment token this opens would be unusable —
            // so the button is hidden rather than shown and failing.
            watchTowerEnabled ? (
              <Button
                variant="contained"
                startIcon={<DevicesOtherIcon sx={{ fontSize: 16 }} />}
                onClick={() => setEnrollOpen(true)}
                sx={{
                  bgcolor: theme.customColors.button.primary,
                  color: theme.customColors.button.text,
                  fontWeight: 500,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "14px",
                  px: 2,
                  py: 1,
                  "&:hover": {
                    bgcolor: theme.palette._blue?.dark || theme.customColors.button.primary,
                  },
                }}
              >
                Connect Device
              </Button>
            ) : null
          }
        />

        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2, pb: 1.5, backgroundColor: theme.customColors.background.main }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <TextField
                  size="small"
                  placeholder="Search your devices…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#6B7280", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchQuery("")} edge="end">
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      fontSize: "14px",
                      "& fieldset": { borderColor: "#E5E7EB" },
                      "&:hover fieldset": { borderColor: "#9CA3AF" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3B82F6",
                        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                      },
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: "10px",
                  backgroundColor: "#F3F4F6",
                  border: "1px solid #E5E7EB",
                  whiteSpace: "nowrap",
                }}
              >
                <Typography variant="body2" color="#374151" fontWeight="500">
                  {deviceCount} {deviceCount === 1 ? "device" : "devices"}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: theme.customColors.border.main }} />

          <ReusableTable
            columns={columns}
            data={scopedRows}
            renderCell={renderCell}
            getRowSx={() => ({})}
            getRowProps={() => ({})}
            loading={loading}
            showPagination={false}
            emptyMessage="No devices enrolled yet — generate a token and paste it in Rego WatchTower."
            sx={{
              "& .MuiTableCell-root": {
                py: "12px !important",
                px: "16px !important",
                borderBottom: "1px solid",
                borderColor: "divider",
              },
              "& .MuiTableRow-root:hover": { bgcolor: "rgba(0, 0, 0, 0.015)" },
            }}
          />
        </CardContent>
      </Card>

      <EnrollDeviceDialog
        open={enrollOpen}
        employee={employeeForDialog}
        onClose={() => {
          setEnrollOpen(false);
          refetch();
        }}
      />

      <AssetDetailsDialog
        open={!!selectedAsset}
        asset={selectedAsset}
        onClose={() => setSelectedAsset(null)}
        onSaved={refetch}
        isEmployeeView
      />
    </Box>
  );
};

export default EmployeeAssets;
